import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { usePOTemplateDnd } from './contextProvider/POTemplateDndContext';

/**
 * ShippingDetails Component
 * Handles shipping information including shipment method, incoterms, shipping costs,
 * port of landing, and port of lading with image upload functionality
 */
export default function ShippingDetails({ isPreviewMode = false }) {
  const { t } = useTranslation('purchase-order');
  const { detailState, setDetailState } = usePOTemplateDnd();
  const [increaseZIndex, setIncreaseZIndex] = useState(false);
  /**
   * Handle input changes for shipping details
   * @param {Event} e - Input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDetailState((prevState) => ({
      ...prevState,
      shippingDetails: {
        ...prevState.shippingDetails,
        [name]: value,
      },
    }));
  };

  const shippingDetails = detailState?.shippingDetails || {};
  const handleMenuOpen = () => {
    setIncreaseZIndex(!increaseZIndex);
  };

  return (
    <div className="inner-container shipping-details-section p-10">
      <div className="section-template-title mb-0">{t('PO_TEMPLATE.SHIPPING_DETAILS.TITLE', 'Shipping Details')}</div>
      <div className="d-flex p-10 ">
        <div className={`common-react-select flex-fill mr-16 mb-0 ${increaseZIndex && 'z-index-6'}`}>
          <p className="field-name">{t('PO_TEMPLATE.SHIPPING_DETAILS.SHIPMENT_METHOD', 'Shipment Method')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shippingDetails.shipmentMethod || '--'}</div>
          ) : (
            <Select
              options={[]}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option._id}
              className="common-select"
              classNamePrefix="common-select"
              menuPosition="fixed"
              placeholder={t('PO_TEMPLATE.SHIPPING_DETAILS.SELECT_METHOD', 'Select Method')}
              value={null}
              isClearable
              maxMenuHeight={150}
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuOpen}
              onChange={(selectedOption) => {
                const event = {
                  target: {
                    name: 'shipmentMethod',
                    value: selectedOption?._id || '',
                  },
                };
                handleInputChange(event);
              }}
              isDisabled
            />
          )}
        </div>
        <div className="common-react-select flex-fill mr-16 mb-0 ">
          <p className="field-name">{t('PO_TEMPLATE.SHIPPING_DETAILS.INCOTERMS', 'Incoterms')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shippingDetails.incoterms || '--'}</div>
          ) : (
            <Select
              options={[]}
              className="common-select"
              classNamePrefix="common-select"
              placeholder={t('PO_TEMPLATE.SHIPPING_DETAILS.SELECT_INCOTERMS', 'Select Incoterms')}
              value={null}
              isClearable
              maxMenuHeight={150}
              onChange={(selectedOption) => {
                const event = {
                  target: {
                    name: 'incoterms',
                    value: selectedOption?.value || '',
                  },
                };
                handleInputChange(event);
              }}
              isDisabled
            />
          )}
        </div>
        <div className="common-input-control flex-fill mr-16 mb-0 ">
          <p className="field-name">{t('PO_TEMPLATE.SHIPPING_DETAILS.SHIPPING_COSTS', 'Shipping and Handling Costs')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shippingDetails.shippingCosts || '--'}</div>
          ) : (
            <input
              type="number"
              name="shippingCosts"
              placeholder={t('PO_TEMPLATE.SHIPPING_DETAILS.SHIPPING_COSTS', 'Enter')}
              className="form-control"
              value={shippingDetails.shippingCosts || ''}
              onChange={handleInputChange}
              min={0}
              disabled
            />
          )}
        </div>
        <div className="common-input-control flex-fill mr-16 mb-0">
          <p className="field-name">{t('PO_TEMPLATE.SHIPPING_DETAILS.PORT_OF_LANDING', 'Port of Landing')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shippingDetails.portOfLanding || '--'}</div>
          ) : (
            <input
              type="text"
              name="portOfLanding"
              placeholder={t('PO_TEMPLATE.SHIPPING_DETAILS.ENTER_PORT', 'Enter')}
              className="form-control"
              value={shippingDetails.portOfLanding || ''}
              onChange={handleInputChange}
              disabled
            />
          )}
        </div>
        <div className="common-input-control flex-fill mr-16 mb-0">
          <p className="field-name">{t('PO_TEMPLATE.SHIPPING_DETAILS.PORT_OF_LOADING', 'Port of Lading')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{shippingDetails.portOfLoading || '--'}</div>
          ) : (
            <input
              type="text"
              name="portOfLoading"
              placeholder={t('PO_TEMPLATE.DETAIL_PAGE.AUTO_FILLED', 'Auto Filled')}
              className="form-control"
              value={shippingDetails.portOfLoading || ''}
              onChange={handleInputChange}
              disabled
            />
          )}
        </div>
      </div>
    </div>
  );
}

ShippingDetails.propTypes = {
  isPreviewMode: PropTypes.bool,
};

ShippingDetails.defaultProps = {
  isPreviewMode: false,
};
